import { Flex, HStack, StackDivider } from "@chakra-ui/react";
import CDS from "design-system";
import { NewHireJourney } from "models/v2/newHireJourney";
import { newHireJourneyStageInfo } from "features/Stage/helpers";
import { getJourneyDayNumber } from "features/NewHireJourney/helpers";
import {
  nextMilestone,
  daysUntilMilestone,
  milestoneCountdownLabel,
  milestoneDateLabel,
} from "features/Stage/milestones";
import { dateInFormat } from "helpers/time";

interface HeaderStageCardProps {
  newHireJourney: NewHireJourney;
  /** Flex sizing for the card. Defaults to "1" for the manager header row; pass "initial" in a vertical stack. */
  flex?: string;
}

function Column({ eyebrow, hero, texture }: { eyebrow: string; hero: string; texture?: string }) {
  return (
    <Flex direction="column" gap={1} flex="1" minW="110px">
      <CDS.Text11 color="content.tertiary" textTransform="uppercase" letterSpacing="wider" fontWeight="600">
        {eyebrow}
      </CDS.Text11>
      <CDS.Text16 fontWeight="600" color="content.primary">
        {hero}
      </CDS.Text16>
      {texture && <CDS.Text12 color="content.tertiary">{texture}</CDS.Text12>}
    </Flex>
  );
}

export default function HeaderStageCard({ newHireJourney, flex = "1" }: HeaderStageCardProps) {
  const { label } = newHireJourneyStageInfo(newHireJourney);
  const timezone = newHireJourney.newHire?.timezone ?? undefined;

  const journeyDay = getJourneyDayNumber(newHireJourney.startDate, timezone);
  const milestone = nextMilestone(journeyDay);
  const countdown = milestoneCountdownLabel(daysUntilMilestone(newHireJourney.startDate, journeyDay, milestone));
  const milestoneDate = milestoneDateLabel(newHireJourney.startDate, milestone);
  const milestoneTexture = [milestoneDate, countdown ? `(${countdown})` : null].filter(Boolean).join(" ") || undefined;

  const startDate = newHireJourney.startDate
    ? dateInFormat(newHireJourney.startDate, "MMM D, YYYY", timezone)
    : "—";
  const startTexture = journeyDay != null ? `Day ${journeyDay}${label ? ` (${label})` : ""}` : undefined;

  return (
    <CDS.Card flex={flex} minW="320px" padding="small">
      <HStack align="stretch" spacing={4} divider={<StackDivider borderColor="gray.200" />}>
        <Column eyebrow="Start" hero={startDate} texture={startTexture} />
        <Column
          eyebrow="Next Milestone"
          hero={milestone?.label ?? "—"}
          texture={milestoneTexture}
        />
      </HStack>
    </CDS.Card>
  );
}
